import { useCallback, useState } from "react";
import { MenuItem, TextField, Typography } from "@mui/material";
import {
  GridFilterOperator,
  GridRenderHeaderFilterProps,
  useGridApiContext,
} from "@mui/x-data-grid-pro";

export const YesNoFilter = (
  props: GridRenderHeaderFilterProps & { operator?: string }
) => {
  const { item, inputRef, operator = "is" } = props;
  const apiRef = useGridApiContext();
  const column = apiRef.current.getColumn(item.field);
  const headerName = column.headerName;

  // Convert the stored filter value to display value
  const getDisplayValue = (value: any) => {
    if (value === true) {
      return "yes";
    }
    if (value === false) {
      return "no";
    }
    if (value === null) {
      return "blank";
    }
    return "";
  };

  const [value, setValue] = useState<string>(getDisplayValue(item.value));

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setValue(newValue);

      // Convert display value to actual filter value
      if (newValue === "") {
        // Empty string means clear the filter
        apiRef.current.deleteFilterItem(item);
      } else if (newValue === "yes") {
        apiRef.current.upsertFilterItem({
          ...item,
          value: true,
          operator,
        });
      } else if (newValue === "no") {
        apiRef.current.upsertFilterItem({
          ...item,
          value: false,
          operator,
        });
      } else if (newValue === "blank") {
        apiRef.current.upsertFilterItem({
          ...item,
          value: null,
          operator,
        });
      }
    },
    [apiRef, item, operator]
  );

  return (
    <TextField
      select
      fullWidth
      label={headerName}
      variant="standard"
      value={value}
      onChange={handleChange}
      inputRef={inputRef as React.RefObject<HTMLInputElement>}
      SelectProps={{
        native: false,
      }}
    >
      <MenuItem value="">
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontStyle: "italic" }}
        >
          -
        </Typography>
      </MenuItem>
      <MenuItem value="yes">Yes</MenuItem>
      <MenuItem value="no">No</MenuItem>
      <MenuItem value="blank">
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontStyle: "italic" }}
        >
          Blank
        </Typography>
      </MenuItem>
    </TextField>
  );
};

export const getYesNoFilterOperator = (): GridFilterOperator[] => {
  return [
    {
      label: "Is",
      value: "is",
      headerLabel: "Is",
      getValueAsString: (filterValue) => {
        if (filterValue === null) {
          return "Blank";
        }
        if (filterValue === true) {
          return "Yes";
        }
        if (filterValue === false) {
          return "No";
        }
        return "";
      },
      getApplyFilterFn: (filterItem) => {
        // Don't filter out false values! Check for undefined only
        if (!filterItem.field || filterItem.value === undefined) {
          return null;
        }

        const filterValue = filterItem.value;

        // Handle blank/null values
        if (filterValue === null) {
          return ({ value }) => {
            return value === null || value === undefined || value === "";
          };
        }

        // Handle Yes (true) values
        if (filterValue === true) {
          return ({ value }) => {
            return value === true || value === "true";
          };
        }

        // Handle No (false) values
        if (filterValue === false) {
          return ({ value }) => {
            return value === false || value === "false";
          };
        }

        // No valid filter value
        return null;
      },
      InputComponent: YesNoFilter,
      InputComponentProps: { type: "singleSelect", operator: "is" },
    },
  ];
};
